import React, { useState, useEffect } from 'react';
import { couponAPI } from '../../services/api';

// Helper functions for coupon validation (matching Flutter app logic)
const isCouponExpired = (coupon) => {
  return new Date() > new Date(coupon.validUntil);
};

const isCouponNotStarted = (coupon) => {
  return new Date() < new Date(coupon.validFrom);
};

const getCouponStatus = (coupon) => {
  if (isCouponExpired(coupon)) return 'expired';
  if (isCouponNotStarted(coupon)) return 'scheduled';
  if (!coupon.isActive) return 'inactive';
  return 'active';
};

const getStatusConfig = (status) => {
  switch (status) {
    case 'expired':
      return { label: 'Expired', color: '#DC2626', bgColor: '#FEE2E2' };
    case 'scheduled':
      return { label: 'Scheduled', color: '#F59E0B', bgColor: '#FEF3C7' };
    case 'inactive':
      return { label: 'Inactive', color: '#64748B', bgColor: '#F1F5F9' };
    case 'active':
    default:
      return { label: 'Active', color: '#10B981', bgColor: '#D1FAE5' };
  }
};

const getUsageLimitText = (coupon) => {
  if (coupon.usageLimit == null) {
    return 'Unlimited';
  }
  return `${coupon.usedCount || 0} / ${coupon.usageLimit} used`;
};

function CouponManagement() {
  const [coupons, setCoupons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderValue: '',
    maxDiscount: '',
    validFrom: '',
    validUntil: '',
    usageLimit: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await couponAPI.getMyCoupons();
      setCoupons(response.data.data.coupons);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // CLIENT-SIDE VALIDATION (defence-in-depth; backend must also enforce)
    const code = (formData.code || '').trim();
    if (code.length < 3) {
      alert('Coupon code must be at least 3 characters');
      return;
    }

    const discount = parseFloat(formData.discountValue);
    if (Number.isNaN(discount) || discount <= 0) {
      alert('Discount value must be greater than 0');
      return;
    }
    if (formData.discountType === 'percentage' && discount > 100) {
      alert('Percentage discount cannot exceed 100%');
      return;
    }

    // Validity window must be coherent: validFrom < validUntil, both present,
    // and validUntil must not be in the past.
    if (!formData.validFrom || !formData.validUntil) {
      alert('Both "valid from" and "valid until" dates are required');
      return;
    }
    const from = new Date(formData.validFrom);
    const until = new Date(formData.validUntil);
    if (Number.isNaN(from.getTime()) || Number.isNaN(until.getTime())) {
      alert('Invalid date format');
      return;
    }
    if (from >= until) {
      alert('"Valid until" must be after "valid from"');
      return;
    }
    if (until < new Date(new Date().toDateString())) {
      alert('"Valid until" cannot be in the past');
      return;
    }

    const minOrder = formData.minOrderValue ? parseFloat(formData.minOrderValue) : 0;
    if (minOrder < 0) {
      alert('Minimum order value cannot be negative');
      return;
    }

    const maxDiscount = formData.maxDiscount ? parseFloat(formData.maxDiscount) : null;
    if (maxDiscount !== null && maxDiscount < 0) {
      alert('Maximum discount cannot be negative');
      return;
    }

    try {
      // Prepare data
      const data = {
        ...formData,
        code,
        discountValue: discount,
        minOrderValue: minOrder,
        maxDiscount,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null
      };

      if (editingCoupon) {
        await couponAPI.update(editingCoupon._id, data);
        alert('Coupon updated successfully!');
      } else {
        await couponAPI.create(data);
        alert('Coupon created successfully!');
      }

      resetForm();
      fetchCoupons();
    } catch (error) {
      alert('Failed to save coupon: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue.toString(),
      minOrderValue: coupon.minOrderValue.toString(),
      maxDiscount: coupon.maxDiscount?.toString() || '',
      validFrom: coupon.validFrom.split('T')[0],
      validUntil: coupon.validUntil.split('T')[0],
      usageLimit: coupon.usageLimit?.toString() || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await couponAPI.delete(id);
        alert('Coupon deleted successfully!');
        fetchCoupons();
      } catch (error) {
        alert('Failed to delete coupon: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await couponAPI.toggleStatus(id);
      fetchCoupons();
    } catch (error) {
      alert('Failed to toggle coupon status: ' + (error.response?.data?.message || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discountType: 'percentage',
      discountValue: '',
      minOrderValue: '',
      maxDiscount: '',
      validFrom: '',
      validUntil: '',
      usageLimit: ''
    });
    setEditingCoupon(null);
    setShowForm(false);
  };

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* Header with Gradient Background */}
        <div style={{
          background: 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px',
          boxShadow: '0 4px 20px rgba(255, 105, 97, 0.15)'
        }} className="fade-in">
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#FFFFFF',
            marginBottom: '8px',
            letterSpacing: '-0.5px'
          }}>
            Coupon Management
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '16px',
            fontWeight: '400'
          }}>
            Create and manage discount coupons for your customers
          </p>
        </div>

        {/* Create Coupon Button */}
        {!showForm && (
          <div style={{ marginBottom: '24px' }}>
            <button
              onClick={() => setShowForm(true)}
              style={{
                background: '#FF6961',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '12px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 105, 97, 0.25)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(255, 105, 97, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 105, 97, 0.25)';
              }}
            >
              <span style={{ fontSize: '20px' }}>+</span>
              <span>Create New Coupon</span>
            </button>
          </div>
        )}

        {/* Coupon Form */}
        {showForm && (
          <div className="fade-in" style={{
            background: '#FFFFFF',
            borderRadius: '16px',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.05)'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '24px',
              color: '#0F172A'
            }}>
              {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {/* Coupon Code */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>Coupon Code*</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="e.g., SAVE20"
                    required
                    style={{
                      textTransform: 'uppercase',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '14px',
                      color: '#0F172A',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  />
                </div>

                {/* Description */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>Description*</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="e.g., Get 20% off on orders above ₹500"
                    required
                    style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '14px',
                      color: '#0F172A',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  />
                </div>

                {/* Discount Type */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>Discount Type*</label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '14px',
                      color: '#0F172A',
                      outline: 'none',
                      background: '#FFFFFF',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>

                {/* Discount Value */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>Discount Value*</label>
                  <input
                    type="number"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    placeholder={formData.discountType === 'percentage' ? 'e.g., 20' : 'e.g., 100'}
                    min="0"
                    step="0.01"
                    required
                    onWheel={(e) => e.target.blur()}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '14px',
                      color: '#0F172A',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  />
                </div>

                {/* Min Order Value */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>Minimum Order Value (₹)</label>
                  <input
                    type="number"
                    name="minOrderValue"
                    value={formData.minOrderValue}
                    onChange={handleInputChange}
                    placeholder="e.g., 500"
                    min="0"
                    step="0.01"
                    onWheel={(e) => e.target.blur()}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '14px',
                      color: '#0F172A',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  />
                </div>

                {/* Max Discount (only for percentage) */}
                {formData.discountType === 'percentage' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0F172A'
                    }}>Max Discount (₹)</label>
                    <input
                      type="number"
                      name="maxDiscount"
                      value={formData.maxDiscount}
                      onChange={handleInputChange}
                      placeholder="e.g., 200"
                      min="0"
                      step="0.01"
                      onWheel={(e) => e.target.blur()}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        fontSize: '14px',
                        color: '#0F172A',
                        outline: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                      onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                    />
                  </div>
                )}

                {/* Valid From */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>Valid From*</label>
                  <input
                    type="date"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '14px',
                      color: '#0F172A',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  />
                </div>

                {/* Valid Until */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>Valid Until*</label>
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '14px',
                      color: '#0F172A',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  />
                </div>

                {/* Usage Limit */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>Usage Limit (leave empty for unlimited)</label>
                  <input
                    type="number"
                    name="usageLimit"
                    value={formData.usageLimit}
                    onChange={handleInputChange}
                    placeholder="e.g., 100"
                    min="1"
                    onWheel={(e) => e.target.blur()}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '14px',
                      color: '#0F172A',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="submit"
                  style={{
                    background: '#FF6961',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(255, 105, 97, 0.25)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    background: '#F1F5F9',
                    color: '#64748B',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#E2E8F0'}
                  onMouseLeave={(e) => e.target.style.background = '#F1F5F9'}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Coupons List */}
        <div className="fade-in" style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.05)'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '24px',
            color: '#0F172A'
          }}>
            Your Coupons
          </h2>

          {coupons.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '64px 32px',
              color: '#64748B'
            }}>
              <div style={{
                fontSize: '64px',
                marginBottom: '16px',
                opacity: '0.5'
              }}>🎫</div>
              <p style={{ fontSize: '16px', fontWeight: '500' }}>No coupons created yet</p>
              <p style={{ fontSize: '14px', marginTop: '8px', color: '#94A3B8' }}>
                Create your first coupon to start offering discounts
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {coupons.map(coupon => {
                const status = getCouponStatus(coupon);
                const statusConfig = getStatusConfig(status);
                const isExpired = status === 'expired';

                return (
                <div key={coupon._id} style={{
                  background: isExpired ? '#FAFAFA' : '#FFFFFF',
                  border: isExpired ? '1px solid rgba(220, 38, 38, 0.3)' : '1px solid #E2E8F0',
                  borderRadius: '12px',
                  padding: '20px',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: isExpired ? 0.8 : 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.08)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  {/* Status Indicator Bar */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: statusConfig.color
                  }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginLeft: '12px' }}>
                    <div style={{ flex: 1 }}>
                      {/* Coupon Code and Status */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          background: status === 'active' ? '#F0FDF4' : statusConfig.bgColor,
                          border: `2px dashed ${statusConfig.color}`,
                          borderRadius: '8px',
                          padding: '8px 16px'
                        }}>
                          <span style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            color: status === 'active' ? '#10B981' : statusConfig.color,
                            fontFamily: 'monospace',
                            letterSpacing: '1px'
                          }}>
                            {coupon.code}
                          </span>
                        </div>
                        <span style={{
                          background: statusConfig.color,
                          color: '#FFFFFF',
                          fontSize: '12px',
                          fontWeight: '600',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {statusConfig.label}
                        </span>
                      </div>

                      {/* Description */}
                      <p style={{
                        color: '#475569',
                        fontSize: '14px',
                        marginBottom: '16px',
                        lineHeight: '1.5'
                      }}>
                        {coupon.description}
                      </p>

                      {/* Coupon Details Grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '12px',
                        padding: '16px',
                        background: '#F8FAFC',
                        borderRadius: '8px'
                      }}>
                        <div>
                          <div style={{
                            fontSize: '12px',
                            color: '#64748B',
                            marginBottom: '4px',
                            fontWeight: '500'
                          }}>Discount</div>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#0F172A'
                          }}>
                            {coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : `₹${coupon.discountValue}`}
                            {coupon.maxDiscount && (
                              <span style={{ fontSize: '12px', fontWeight: '500', color: '#64748B', marginLeft: '4px' }}>
                                (Max: ₹{coupon.maxDiscount})
                              </span>
                            )}
                          </div>
                        </div>

                        <div>
                          <div style={{
                            fontSize: '12px',
                            color: '#64748B',
                            marginBottom: '4px',
                            fontWeight: '500'
                          }}>Min Order</div>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#0F172A'
                          }}>₹{coupon.minOrderValue}</div>
                        </div>

                        <div>
                          <div style={{
                            fontSize: '12px',
                            color: '#64748B',
                            marginBottom: '4px',
                            fontWeight: '500'
                          }}>Valid Period</div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: isExpired ? '#DC2626' : (status === 'scheduled' ? '#F59E0B' : '#0F172A')
                          }}>
                            {new Date(coupon.validFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(coupon.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        </div>

                        <div>
                          <div style={{
                            fontSize: '12px',
                            color: '#64748B',
                            marginBottom: '4px',
                            fontWeight: '500'
                          }}>Usage</div>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#0F172A'
                          }}>
                            {getUsageLimitText(coupon)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '20px' }}>
                      {/* Toggle Status - Switch Style */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px'
                      }}>
                        <div
                          onClick={() => handleToggleStatus(coupon._id)}
                          style={{
                            width: '48px',
                            height: '26px',
                            background: status === 'active' ? '#10B981' : (isExpired ? '#DC2626' : '#CBD5E1'),
                            borderRadius: '13px',
                            position: 'relative',
                            cursor: isExpired ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            opacity: isExpired ? 0.6 : 1
                          }}
                          title={isExpired ? 'Cannot toggle expired coupon' : (coupon.isActive ? 'Deactivate' : 'Activate')}
                        >
                          <div style={{
                            width: '22px',
                            height: '22px',
                            background: '#FFFFFF',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: coupon.isActive ? '24px' : '2px',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                          }} />
                        </div>
                      </div>

                      <button
                        onClick={() => handleEdit(coupon)}
                        style={{
                          background: '#F1F5F9',
                          color: '#64748B',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#E2E8F0';
                          e.target.style.color = '#475569';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#F1F5F9';
                          e.target.style.color = '#64748B';
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(coupon._id)}
                        style={{
                          background: '#FEE2E2',
                          color: '#DC2626',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#FCA5A5';
                          e.target.style.color = '#FFFFFF';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#FEE2E2';
                          e.target.style.color = '#DC2626';
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CouponManagement;
